import { ChangeDetectionStrategy, Component, effect, inject, input, output, signal } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { ToastrService } from 'ngx-toastr';
import type { MitMandateDTO } from '../../core/models';
import { MitService, mitApiError } from '../../services/mit.service';
import { MitLimitsFieldsComponent, MitLimitsForm, buildMitLimitsForm, mitLimitsFromForm } from './mit-limits-fields.component';

@Component({
  selector: 'app-mit-mandate-limits-dialog',
  templateUrl: './mit-mandate-limits-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [DialogModule, ButtonModule, MitLimitsFieldsComponent]
})
export class MitMandateLimitsDialogComponent {
  private fb = inject(FormBuilder);
  private mitService = inject(MitService);
  private toastr = inject(ToastrService);

  mandate = input<MitMandateDTO | null>(null);
  visible = input(false);
  closed = output<void>();
  changed = output<MitMandateDTO>();

  form = signal<MitLimitsForm>(buildMitLimitsForm(this.fb));
  saving = signal(false);
  error = signal<string | null>(null);

  constructor() {
    effect(() => {
      if (this.visible()) {
        this.form.set(buildMitLimitsForm(this.fb, this.mandate()?.limits));
        this.error.set(null);
      }
    });
  }

  close(): void {
    if (!this.saving()) this.closed.emit();
  }

  save(): void {
    const mandate = this.mandate();
    const form = this.form();
    form.markAllAsTouched();
    if (!mandate || form.invalid || this.saving()) return;
    this.saving.set(true);
    this.error.set(null);
    this.mitService.updateLimits(mandate.id, mitLimitsFromForm(form)).subscribe({
      next: (response) => {
        this.saving.set(false);
        const pending = response.metadata?.pendingConsent === true;
        if (pending) {
          this.toastr.info('El aumento quedó pendiente: el cliente debe aceptarlo desde la liga que le enviamos por correo.');
        } else {
          this.toastr.success(response.message || 'Límites actualizados');
        }
        if (response.body) this.changed.emit(response.body);
        this.closed.emit();
      },
      error: (err) => {
        this.saving.set(false);
        this.error.set(mitApiError(err, 'No se pudieron cambiar los límites').message);
      }
    });
  }
}
